import classnames from 'classnames/bind';
import React from 'react';

import { ClickhouseCursorParams } from 'dashboards/types/pagination';
import { DataWidget } from 'types/dashboard_widget';

import { DATA_POOLS } from 'dashboards/services/data_pool';
import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';
import { createCsvFile } from 'services/csv';
import { downloadFile } from 'services/download_file';
import { rowsToTimezone } from 'dashboards/services/table';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useDataGrid } from 'dashboards/hooks/useDataGrid';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTopLevelDataWidgetFilters } from 'dashboards/hooks/useTopLevelDataWidgetFilters';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';
import { CursorPagination } from 'components/Pagination/CursorPagination';
import { Icon } from 'components/Icon/IconDeprecated';
import { Table } from 'dashboards/components/Table/Table';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './Chart.module.scss';

const c = classnames.bind(styles);

type DataGridProps = {
  widget: DataWidget<'table'>;
};

export function DataGrid({ widget }: DataGridProps) {
  const { start, stop, timezone } = useTimeRangeParams();

  const filters = useTopLevelDataWidgetFilters({
    filters: sanitizeDataWidgetFilters(widget.query.filters),
    columns: DATA_POOLS[widget.query.dataPool.name].columns,
  });

  const [cursor, setCursor] = React.useState<ClickhouseCursorParams>({});
  const [page, setPage] = React.useState(1);

  const {
    data,
    isLoading: isLoadingDataGrid,
    isFetching,
    isPreviousData,
    error,
  } = useDataGrid({
    params: {
      timeRange: { start, stop },
      timeZone: timezone,
      ...widget.query,
      filters,
      ...cursor,
    },
    refetchInterval: WIDGET_REFETCH_INTERVAL,
    keepPreviousData: true,
  });

  const isLoading = isLoadingDataGrid || (isFetching && isPreviousData);

  function handleNextPage() {
    if (!data) {
      return;
    }

    setPage((prev) => prev + 1);
    setCursor({ after: data.pageInfo.endCursor });
  }

  function handlePreviousPage() {
    if (!data) {
      return;
    }

    setPage((prev) => Math.max(1, prev - 1));
    setCursor({ before: data.pageInfo.startCursor });
  }

  if (error) {
    return (
      <Widget widget={widget}>
        <ChartError />
      </Widget>
    );
  }

  if (!data && isLoadingDataGrid) {
    return (
      <Widget widget={widget}>
        <ChartLoader />
      </Widget>
    );
  }

  if (!data) {
    return null;
  }

  const { headers, rows, pageInfo } = data;

  return (
    <Widget
      widget={widget}
      menuItems={{
        csv: {
          children: 'Export as CSV',
          leftSection: <Icon name="export" size="small" />,
          onClick: () =>
            downloadFile(
              widget.name,
              createCsvFile(rowsToTimezone({ headers, rows, timezone }))
            ),
          disabled: rows.length === 0,
        },
      }}
    >
      <div className={c('chart')}>
        <div className="flex flex-col justify-between">
          <Table headers={headers} rows={rows} isLoading={isLoading} />
          <CursorPagination
            page={page}
            hasNextPage={pageInfo.hasNextPage}
            hasPreviousPage={pageInfo.hasPreviousPage}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
            disabled={isLoading}
          />
        </div>
      </div>
    </Widget>
  );
}
